import { inject, Injectable } from '@angular/core';
import { UuidService } from '../../services/uuid.service';
import { StoreBatchWeighingSaveInfo } from '../../storage/data-store/model/store-batch-weighing-save-info';
import { SyncStoreSaveData } from '../../storage/sync-store/update-data/sync-store-save-data';
import { Uuid } from '../../domain/model/uuid/uuid.type';
import { SyncStoreUpdateBatchWeighing } from '../../storage/sync-store/update-data/sync-store-update-batch-weighing';
import { SyncStoreUpdateAnimalWeighing } from '../../storage/sync-store/update-data/sync-store-update-animal-weighing';
import { StoreAnimalWeighingSaveInfo } from '../../storage/data-store/model/store-animal-weighing-save-info';
import { SyncStoreLastServerSyncInfo } from '../../storage/sync-store/model/sync-store-last-server-sync-info';
import { SyncStoreAnimalWeight } from '../../storage/sync-store/model/sync-store-animal-weight';
import { StoreAnimalWeights } from '../../storage/data-store/model/store-animal-weights';
import { UserInfo } from '../../domain/model/user-info';



@Injectable({
  providedIn: 'root',
})
export class SaveToSyncMapper {
  private readonly uuidService = inject(UuidService);
  constructor() {}

  mapBatchWeighingSyncCommand(
    batchSaveInfo: StoreBatchWeighingSaveInfo,
    saveTime: Date
  ): SyncStoreSaveData<SyncStoreUpdateBatchWeighing> {
    const batchWeighingStoreCommand = batchSaveInfo.batchWeighingStoreCommand;

    if (batchWeighingStoreCommand === undefined || batchWeighingStoreCommand === null) {
      throw new Error('BatchWeighingStoreCommand is undefined or null');
    }
    const syncData = new SyncStoreSaveData<SyncStoreUpdateBatchWeighing>(
      this.uuidService.getNewUuid().asString(),
      saveTime,
      batchSaveInfo.user,
      [
        new SyncStoreUpdateBatchWeighing(
          batchWeighingStoreCommand.id,
          this.mapToLastServerSyncInfo(
            batchWeighingStoreCommand.updateInfo.lastServerUpdate,
            batchWeighingStoreCommand.updateInfo.lastLocalUpdate,
            batchWeighingStoreCommand.updateInfo.lastEditedByUser
          ),
          batchSaveInfo.batchWeighingId,
          batchSaveInfo.batchId,
          batchSaveInfo.sectionId,
          batchWeighingStoreCommand.gender
        ),
      ]
    );

    return syncData;
  }

  mapAnimalWeighingSyncCommand(
    batchSaveInfo: StoreBatchWeighingSaveInfo,
    saveTime: Date
  ): SyncStoreSaveData<SyncStoreUpdateAnimalWeighing> {
    const syncData = new SyncStoreSaveData<SyncStoreUpdateAnimalWeighing>(
      this.uuidService.getNewUuid().asString(),
      saveTime,
      batchSaveInfo.user,
      this.mapAnimalSaveInfoToSync(
        batchSaveInfo.animalWeighings,
        batchSaveInfo.sectionId,
        batchSaveInfo.batchWeighingId,
        batchSaveInfo.batchId
      )
    );
    return syncData;
  }

  private mapAnimalSaveInfoToSync(
    animalWeights: StoreAnimalWeighingSaveInfo[],
    sectionId: Uuid,
    batchWeighingId: Uuid,
    batchId: Uuid
  ): SyncStoreUpdateAnimalWeighing[] {
    const mapped: SyncStoreUpdateAnimalWeighing[] = animalWeights.map((weights) => {
      return new SyncStoreUpdateAnimalWeighing(
        weights.id,
        this.mapToLastServerSyncInfo(
          weights.update.lastServerUpdate,
          weights.update.lastLocalUpdate,
          weights.update.lastEditedByUser
        ),
        batchWeighingId,
        batchId,
        sectionId,
        this.mapAnimalWeightToSync(weights.animalWeights)
      );
    });
    return mapped;
  }

  private mapToLastServerSyncInfo(
    lastServerUpdate: Date | null,
    lastLocalUpdate: Date | null,
    lastEditedByUser: UserInfo | null
  ): SyncStoreLastServerSyncInfo {
    return new SyncStoreLastServerSyncInfo(lastServerUpdate, lastLocalUpdate, lastEditedByUser);
  }

  private mapAnimalWeightToSync(animalWeights: StoreAnimalWeights): SyncStoreAnimalWeight {
    return new SyncStoreAnimalWeight(animalWeights.animals, animalWeights.weight);
  }
}
